<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';

import { IconChevronDown, IconPreloader } from '@/shared/ui/icons';

import type { DropdownMenuItem, DropdownContentConfig } from './types';

import { useDropdown } from './composables/use-dropdown';

interface Props {
  label?: string;
  items?: DropdownMenuItem[];
  content?: DropdownContentConfig;
  width?: string;
  /** Жёсткий потолок высоты поповера. Превалирует над `content.maxHeight`. */
  maxHeight?: number;
  showChevron?: boolean;
  disabled?: boolean;
  /** Рендерить overlay и floating-content в body. Отключается для вложенных popover внутри modal. */
  teleport?: boolean;
  /** Дополнительный класс на обёртку триггера (используется как floating-ui anchor). */
  triggerClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  items: undefined,
  content: undefined,
  width: 'auto',
  maxHeight: undefined,
  showChevron: true,
  disabled: false,
  teleport: true,
  triggerClass: '',
});

const resolvedMaxHeight = computed(() => props.maxHeight ?? props.content?.maxHeight);

const {
  isOpen,
  triggerRef,
  floatingRef,
  floatingStyles,
  availableHeight,
  isPositioned,
  transformOrigin,
  toggle,
  close,
} = useDropdown({
  offset: props.content?.sideOffset ?? 8,
  align: props.content?.align,
  side: props.content?.side,
  flip: props.content?.flip,
  shift: props.content?.shift,
  maxHeight: resolvedMaxHeight.value,
});

// triggerRef используется в template через ref="triggerRef"
void triggerRef;
void floatingRef;

const popoverStyle = computed<CSSProperties>(() => ({
  ...floatingStyles.value,
  width: props.width,
  maxHeight: availableHeight.value != null ? `${availableHeight.value}px` : undefined,
  visibility: isPositioned.value ? undefined : 'hidden',
  transformOrigin: transformOrigin.value,
}));

const handleItemClick = async (item: DropdownMenuItem) => {
  if (item.disabled || item.loading || !item.onClick) return;
  await item.onClick(item);
  close();
};
</script>

<template>
  <div>
    <!-- Автоматический триггер с label -->
    <div
      v-if="label"
      ref="triggerRef"
    >
      <button
        class="flex items-center gap-2 rounded-lg bg-(--secondary-button-bg-color) px-4 py-2 text-(--base-popup-text-color) transition-colors duration-200 hover:bg-(--secondary-button-hover-bg-color)"
        :disabled="disabled"
        @click="toggle"
      >
        <span class="text-sm font-medium">{{ label }}</span>
        <IconChevronDown
          v-if="showChevron"
          class="transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
          :width="10"
          :height="10"
        />
      </button>
    </div>

    <!-- Кастомный триггер через slot -->
    <div
      v-else
      ref="triggerRef"
      :class="triggerClass"
    >
      <slot
        name="trigger"
        :is-open="isOpen"
        :toggle="toggle"
      />
    </div>

    <!-- Меню -->
    <Teleport
      to="body"
      :disabled="!teleport"
    >
      <!-- Overlay для закрытия меню при клике вне его -->
      <div
        v-if="isOpen"
        data-modal-ignore
        class="fixed inset-0 z-999998"
        @click="close"
      />

      <!-- Выпадающее меню -->
      <Transition
        enter-active-class="transition-[opacity,scale] duration-[180ms] ease-snappy"
        enter-from-class="opacity-0 scale-[0.96] motion-reduce:scale-100"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-opacity duration-100 ease-snappy"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          ref="floatingRef"
          data-modal-ignore
          class="base-dropdown-popup z-999999 overflow-y-auto rounded-lg border border-(--base-popup-border-color) bg-(--base-popup-bg) shadow-xl"
          :style="popoverStyle"
          @click.stop
        >
          <!-- Автоматический рендер items -->
          <div
            v-if="items"
            class="py-1"
          >
            <template
              v-for="(item, index) in items"
              :key="item.id || index"
            >
              <div
                v-if="item.divider"
                class="my-1 h-px bg-(--base-popup-border-color)"
              />
              <button
                v-else
                class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-(--base-popup-text-color) transition-colors duration-150 hover:bg-(--base-popup-hover-color)"
                :class="{
                  'cursor-not-allowed opacity-50': item.disabled || item.loading,
                }"
                :disabled="item.disabled || item.loading"
                @click="handleItemClick(item)"
              >
                <component
                  :is="item.icon"
                  v-if="item.icon && !item.loading"
                  :width="16"
                  :height="16"
                />
                <span>{{ item.label }}</span>
                <IconPreloader
                  v-if="item.loading"
                  class="ml-auto"
                  :width="16"
                  :height="16"
                />
              </button>
            </template>
          </div>

          <!-- Кастомный контент через slot -->
          <slot
            v-else
            name="content"
            :close="close"
          />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
